import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useLanguage } from "context/LanguageContext";
import { useNotification } from "context/NotificationContext";
import { handleNavigateBack, renderSelectField, renderYesNoField, validateFields, renderInputTextField, convertYesNoFieldsToString } from "components/formUtils";

const AnestesiaForm = () => {
  const { GetTranslation } = useLanguage();
  const notify = useNotification();
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    uso_intubacao_seletiva: "",
    metodo_intubacao_seletiva: "",
    bloqueio_regional: "",
    tecnica_bloqueio_regional: [],
    momento_bloqueio_regional: [],
    monitorizacao_debito_min_invasiva: "",
    monitor_min_invasivo_utilizado: "",
    monitorizacao_debito_invasiva: "",
    monitor_invasivo_utilizado: "",
    monitorizacao_snc: "",
    tipo_monitorizacao_snc: [],
    manutencao_ventilacao_cec: "",
    consumo_total_opioides: "",
    uso_dexmedetomidina: "",
    extubacao_em_sala: ""
  });

  const token = localStorage.getItem("token");

  const yesNoFields = [
    "uso_intubacao_seletiva",
    "bloqueio_regional",
    "monitorizacao_debito_min_invasiva",
    "monitorizacao_debito_invasiva",
    "monitorizacao_snc",
    "manutencao_ventilacao_cec",
    "uso_dexmedetomidina",
    "extubacao_em_sala"
  ];

  useEffect(() => {
    if (id) {
      fetch(`${process.env.REACT_APP_API_URL}/anestesia/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            // Converte os campos de Sim/Não para strings
            const convertedData = convertYesNoFieldsToString(data.data, yesNoFields);
            setFormData(convertedData);
          } else {
            notify({
              type: "error",
              message: data.error || "Erro ao buscar os dados",
            });
          }
        })
        .catch(() => {
          notify({
            type: "error",
            message: "Erro ao buscar os dados",
          });
        });
    }
  }, [id, token, notify]);
  

  const handleSubmit = () => {
    const requiredFields = ["uso_intubacao_seletiva", "bloqueio_regional", "monitorizacao_debito_min_invasiva", "monitorizacao_debito_invasiva", "monitorizacao_snc", "manutencao_ventilacao_cec", "consumo_total_opioides", "uso_dexmedetomidina", "extubacao_em_sala"];
    if (!validateFields(formData, requiredFields, GetTranslation, notify)) return;

    const apiUrl = id
      ? `${process.env.REACT_APP_API_URL}/anestesia/update/${id}`
      : `${process.env.REACT_APP_API_URL}/anestesia/add`;

    const method = id ? "PUT" : "POST";

    fetch(apiUrl, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        if (response.ok) {
          notify({
            type: "success",
            message: id ? "Anestesia atualizada com sucesso!" : "Anestesia adicionada com sucesso!",
          });
        } else {
          response.json().then((data) => {
            notify({
              type: "error",
              message: `Erro: ${data.error}`,
            });
          });
        }
      })
      .catch(() => {
        notify({
          type: "error",
          message: "Erro ao enviar os dados",
        });
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const confirmSave = (e) => {
    e.preventDefault();
    confirmDialog({
      message: GetTranslation("Confirmar_salvar"),
      header: GetTranslation("Confirmar"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: GetTranslation("Yes"),
      rejectLabel: GetTranslation("No"),
      accept: handleSubmit,
      reject: () => {
        notify({
          type: "info",
          message: GetTranslation("Operacao_cancelada"),
        });
      },
    });
  };

  const handleBack = handleNavigateBack(navigate, id);


  return (
    <div className="card p-3">
      <ConfirmDialog />
      <h4>{id ? GetTranslation("Edit_Anestesia") : GetTranslation("Add_Anestesia")}</h4>
      <form className="row g-3">
        {/* Uso de intubação seletiva */}
        {renderYesNoField(formData, handleChange, "uso_intubacao_seletiva", "Uso de intubação seletiva", GetTranslation)}

        {/* Método de intubação seletiva */}
        {formData.uso_intubacao_seletiva === "1" &&
          renderSelectField(
            formData,
            handleChange,
            "metodo_intubacao_seletiva",
            "Método de intubação seletiva",
            ["Robertshaw", "Sonda de oclusão brônquica"],
            GetTranslation
          )}

        {/* Bloqueio regional */}
        {renderYesNoField(formData, handleChange, "bloqueio_regional", "Bloqueio regional", GetTranslation)}

        {/* Técnica de bloqueio regional */}
        {formData.bloqueio_regional === "1" &&
          renderSelectField(
            formData,
            handleChange,
            "tecnica_bloqueio_regional",
            "Técnica de bloqueio regional",
            ["Eretor da espinha", "Paraesternal", "Subcostal", "Intercostal", "Serrátil anterior"],
            GetTranslation,
          )}

        {/* Momento do bloqueio regional */}
        {formData.bloqueio_regional === "1" &&
          renderSelectField(
            formData,
            handleChange,
            "momento_bloqueio_regional",
            "Momento do bloqueio regional",
            ["Pré-incisional", "Pós-incisional", "Na UTI"],
            GetTranslation,
          )}

        {/* Monitorização de débito minimamente invasiva */}
        {renderYesNoField(formData, handleChange, "monitorizacao_debito_min_invasiva", "Monitorização de débito minimamente invasiva", GetTranslation)}

        {/* Monitor minimamente invasivo utilizado */}
        {formData.monitorizacao_debito_min_invasiva === "1" &&
          renderSelectField(
            formData,
            handleChange,
            "monitor_min_invasivo_utilizado",
            "Monitor minimamente invasivo utilizado",
            ["FloTrac/Vigileo", "PICCO", "LidCO"],
            GetTranslation
          )}

        {/* Monitorização de débito invasiva */}
        {renderYesNoField(formData, handleChange, "monitorizacao_debito_invasiva", "Monitorização de débito invasiva", GetTranslation)}

        {/* Monitor invasivo utilizado */}
        {formData.monitorizacao_debito_invasiva === "1" &&
          renderSelectField(
            formData,
            handleChange,
            "monitor_invasivo_utilizado",
            "Monitor invasivo utilizado",
            ["Swan-Ganz", "Termodiluição transpulmonar"],
            GetTranslation
          )}

        {/* Monitorização de SNC */}
        {renderYesNoField(formData, handleChange, "monitorizacao_snc", "Monitorização de SNC", GetTranslation)}

        {/* Tipo de monitorização de SNC */}
        {formData.monitorizacao_snc === "1" &&
          renderSelectField(
            formData,
            handleChange,
            "tipo_monitorizacao_snc",
            "Tipo de monitorização de SNC",
            ["BIS", "QNox", "NIRS", "Doppler transcraniano", "Saturação venosa jugular"],
            GetTranslation,
          )}

        {/* Manutenção de ventilação durante a CEC */}
        {renderYesNoField(formData, handleChange, "manutencao_ventilacao_cec", "Manutenção de ventilação durante a CEC", GetTranslation)}

        {/* Consumo total de opioides */}
        {renderInputTextField(formData, handleChange, "consumo_total_opioides", "Consumo total de opioides", GetTranslation, "(e.g. Morfina 10 mg e Fentanil 300 mcg)")}

        {/* Uso de dexmedetomidina */}
        {renderYesNoField(formData, handleChange, "uso_dexmedetomidina", "Uso de dexmedetomidina", GetTranslation)}

        {/* Extubação em sala */}
        {renderYesNoField(formData, handleChange, "extubacao_em_sala", "Extubação em sala", GetTranslation)}

        <div className="col-12 d-flex justify-content-between">
          <Button label={GetTranslation("Voltar")} className="btn btn-secondary" onClick={handleBack} />
          <Button label={id ? GetTranslation("Update") : GetTranslation("Create")} className="btn btn-primary" type="submit" onClick={confirmSave} />
        </div>
      </form>
    </div>
  );
};

export default AnestesiaForm;